import { Coins } from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useGetOne,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";

import type { CreditBalance, Sale } from "../types";
import { useCreditPrices } from "./creditPrices";
import {
  creditLevel,
  seesCreditGauge,
  shortCount,
  type CreditLevel,
} from "./creditLevel";

// Même requête que la section Contacts de la fiche : une recherche lancée ou close là-bas met le compteur à jour.
const useOwnCreditBalance = () => {
  const { data: identity } = useGetIdentity();
  return useGetList<CreditBalance>(
    "credit_balances",
    { filter: { id: identity?.id }, pagination: { page: 1, perPage: 1 } },
    { enabled: !!identity, refetchInterval: 60_000 },
  );
};

const RAIL_COLORS: Record<CreditLevel, string> = {
  ok: "text-[#b4becb] hover:text-white",
  low: "text-amber-300",
  empty: "text-red-300",
};
const HEADER_COLORS: Record<CreditLevel, string> = {
  ok: "text-muted-foreground",
  low: "text-inferred-ink",
  empty: "text-destructive",
};

/** Solde disponible du côté (équipe du client ou organisation), toujours visible ; un clic ouvre l'écran des crédits. */
export const CreditGauge = ({ variant }: { variant: "rail" | "header" }) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { data: identity } = useGetIdentity();
  const { data: sale } = useGetOne<Sale>(
    "sales",
    { id: identity?.id ?? 0 },
    { enabled: identity?.id != null },
  );
  const { data: balances } = useOwnCreditBalance();
  const prices = useCreditPrices();
  const credit = balances?.[0];
  if (!credit || !seesCreditGauge(sale)) return null;

  const available = credit.balance - credit.reserved;
  const level = creditLevel(available, prices?.mobile ?? 20);
  const label = [
    translate(
      credit.org_name
        ? "resources.projects.credits.gauge_org"
        : "resources.projects.credits.gauge",
      {
        smart_count: available,
        count: available.toLocaleString(locale),
        org: credit.org_name,
      },
    ),
    credit.reserved > 0 &&
      translate("resources.projects.credits.reserved", {
        reserved: credit.reserved,
      }),
    level !== "ok" && translate(`resources.projects.credits.gauge_${level}`),
  ]
    .filter(Boolean)
    .join(" · ");

  if (variant === "rail") {
    return (
      <Link
        to="/credit_balances"
        title={label}
        aria-label={label}
        className={`flex w-[72px] flex-col items-center gap-0.5 rounded-md py-1.5 no-underline transition-colors hover:bg-sidebar-accent/60 focus-visible:outline-2 focus-visible:outline-sidebar-ring ${RAIL_COLORS[level]}`}
      >
        <Coins className="size-5" aria-hidden />
        <span className="text-[11px] font-semibold leading-4 tabular-nums">
          {shortCount(available, locale)}
        </span>
      </Link>
    );
  }
  return (
    <Link
      to="/credit_balances"
      title={label}
      aria-label={label}
      // Empilé : l'en-tête du téléphone est déjà serré (recherche, filtres, alertes).
      className={`flex shrink-0 flex-col items-center px-1 text-[10px] font-semibold leading-3 tabular-nums no-underline ${HEADER_COLORS[level]}`}
    >
      <Coins className="size-4" aria-hidden />
      {shortCount(available, locale)}
    </Link>
  );
};
